import { useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import actions from 'store/actions';
import selectors from 'store/selectors';

// MUI
import { Box, Divider, Typography, IconButton } from '@mui/material';
// Icons
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Custom components
import CustomModal from 'components/CustomModal';
import CustomToolTip from 'components/Tooltip';
import ZoomInIcon from 'components/Icons/zoomInIcon';
import ZoomOutIcon from 'components/Icons/zoomOutIcon';

// Styles
import {
  ArrowsContainer,
  ImageContainer,
  HeaderContent,
  ButtonsContainer,
  ImageBox,
} from './ImagePreviewModal.styles';

// Constants
import DIALOGS from 'utils/dialogIds';

// Slider
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Moment
import DeleteIcon from 'components/Icons/deleteIcon';

const ImagePreviewModal = () => {
  const dispatch = useDispatch();
  const dialogId = DIALOGS.PHOTOS_ATTACHMENTS_PREVIEW;
  const _dialogState: any = useSelector(selectors.getDialogState);
  const dialogState = _dialogState?.[dialogId];
  const { data, open } = dialogState;
  const { removeItem } = data;
  const [zoomLevels, setZoomLevels] = useState(
    data?.allAttachments.map(() => 1),
  );
  const initialSlide = data?.allAttachments?.findIndex(
    (attachment) =>
      attachment?.fileUrl === data?.id || attachment?.url === data?.id,
  );
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const handleClose = () => {
    dispatch(actions.closeDialog(dialogId));
  };
  const handleZoomIn = () => {
    setZoomLevels((prevZoomLevels) => {
      const newZoomLevels = [...prevZoomLevels];
      newZoomLevels[currentSlide] = Math.min(
        newZoomLevels[currentSlide] + 0.2,
        3,
      );
      return newZoomLevels;
    });
  };
  const handleZoomOut = () => {
    setZoomLevels((prevZoomLevels) => {
      const newZoomLevels = [...prevZoomLevels];
      newZoomLevels[currentSlide] = Math.max(
        newZoomLevels[currentSlide] - 0.2,
        1,
      );
      return newZoomLevels;
    });
  };

  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomToolTip
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              color="white"
              fontWeight={600}
              lineHeight="16.39px"
            >
              Previous
            </Typography>
            <ArrowsContainer>
              <ArrowBackIcon fontSize="inherit" />
            </ArrowsContainer>
          </Box>
        }
        arrow={false}
        placement="top"
        PopperProps={{
          sx: {
            '.MuiTooltip-tooltip': {
              marginBottom: '8px !important',
              background: '#212121 !important',
            },
          },
        }}
      >
        <div
          className={className}
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onClick}
        />
      </CustomToolTip>
    );
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomToolTip
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              color="white"
              fontWeight={600}
              lineHeight="16.39px"
            >
              Next
            </Typography>
            <ArrowsContainer>
              <ArrowForwardIcon fontSize="inherit" />
            </ArrowsContainer>
          </Box>
        }
        arrow={false}
        placement="top"
        PopperProps={{
          sx: {
            '.MuiTooltip-tooltip': {
              marginBottom: '8px !important',
              background: '#212121 !important',
            },
          },
        }}
      >
        <div
          className={className}
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onClick}
        />
      </CustomToolTip>
    );
  };

  const settings = {
    initialSlide: currentSlide,
    afterChange: (current: number) => {
      setCurrentSlide(current);
      setZoomLevels(data?.allAttachments.map(() => 1));
    },
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  const currentAttachment = data?.allAttachments[currentSlide];
  const isVideo =
    currentAttachment?.fileType === 'video' ||
    currentAttachment?.type?.includes('video');
  const isPdf =
    currentAttachment?.fileType === 'pdf' ||
    currentAttachment?.type?.includes('pdf');
  return (
    <CustomModal
      maxWidth={false}
      open={open}
      handleClose={handleClose}
      sx={{
        '.MuiPaper-root': {
          flexDirection: 'row',
          width: { xs: '100%', md: '1024px' },
          height: 'fit-content',
          maxHeight: '80vh',
        },
      }}
    >
      <Box sx={{ width: '100%', height: '100%' }}>
        <ImageContainer>
          <HeaderContent>
            <Typography
              variant="h5"
              fontWeight={700}
              lineHeight="32.78px"
              color="black"
              overflow="hidden"
              noWrap
              textOverflow="ellipsis"
            >
              {currentAttachment?.name || 'Photo'}
            </Typography>
            <ButtonsContainer>
              {!isVideo ? (
                <>
                  <CustomToolTip
                    title="Zoom Out"
                    arrow={false}
                    placement="top"
                    PopperProps={{
                      sx: {
                        '.MuiTooltip-tooltip': {
                          marginBottom: '6px !important',
                          background: '#212121 !important',
                        },
                      },
                    }}
                  >
                    <IconButton onClick={handleZoomOut}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </CustomToolTip>
                  <CustomToolTip
                    title="Zoom In"
                    arrow={false}
                    placement="top"
                    PopperProps={{
                      sx: {
                        '.MuiTooltip-tooltip': {
                          marginBottom: '6px !important',
                          background: '#212121 !important',
                        },
                      },
                    }}
                  >
                    <IconButton onClick={handleZoomIn}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </CustomToolTip>
                </>
              ) : null}
              {removeItem ? (
                <CustomToolTip
                  title="Delete"
                  arrow={false}
                  placement="top"
                  PopperProps={{
                    sx: {
                      '.MuiTooltip-tooltip': {
                        marginBottom: '6px !important',
                        background: '#212121 !important',
                      },
                    },
                  }}
                >
                  <IconButton
                    color={'error'}
                    sx={{
                      bgcolor: '#FFEBEE',
                      color: '#D32F2F',
                      '&:hover': { bgcolor: '#FFEBEE' },
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeItem?.(event, currentSlide);
                      handleClose();
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CustomToolTip>
              ) : null}
              <Divider
                orientation="vertical"
                sx={{ border: '1px solid #E0E0E0', height: '36px' }}
              ></Divider>
              <IconButton onClick={handleClose}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </ButtonsContainer>
          </HeaderContent>
          <Slider {...settings}>
            {data.allAttachments.map((item, index) => (
              <ImageBox key={index}>
                {item?.fileType === 'video' || item?.type?.includes('video') ? (
                  <video
                    controls
                    autoPlay
                    style={{
                      maxWidth: '100%',
                      maxHeight: '80vh',
                      width: '100%',
                    }}
                  >
                    <source src={item?.fileUrl || item?.url} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <>
                    {isPdf ? (
                      <Box sx={{ height: '70vh', width: '100%' }}>
                        <iframe
                          src={`${item?.fileUrl || item?.url}#toolbar=0`}
                          title={item?.name}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                        />
                      </Box>
                    ) : (
                      <img
                        src={item?.fileUrl || item?.url}
                        alt={`Slide ${index + 1}`}
                        style={{ transform: `scale(${zoomLevels[index]})` }}
                      />
                    )}
                  </>
                )}
              </ImageBox>
            ))}
          </Slider>
        </ImageContainer>
      </Box>
    </CustomModal>
  );
};

export default ImagePreviewModal;
